import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { BackButton, Loader, Toast } from "@egovernments/digit-ui-react-components";
import {
  Table,
  TableContainer,
  Checkbox,
  FormControlLabel,
  Popover,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  TablePagination,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

const EditNoc = ({ departmentsList, nocSearchData, isLoading, tenantId, setEditMode, refetch }) => {
  // Prepare columns
  const history = useHistory();
  const { t } = useTranslation()
  const baseColumns = [
    { id: "srNo", label: "Sr. No", filterable: false },
    { id: "district", label: "District", filterable: true },
    { id: "tehsil", label: "Tehsil", filterable: true },
    { id: "village", label: "Village", filterable: true },
  ];
  const deptColumns = departmentsList.map((dept) => ({
    id: dept.code,
    label: dept.name,
    filterable: true,
  }));
  const allColumns = [...baseColumns, ...deptColumns];

  // State for UI controls
  const [selectedColumns, setSelectedColumns] = useState(allColumns.map((col) => col.id));
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState({});
  const [globalSearch, setGlobalSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showToast, setShowToast] = useState(null);

  // Filtering logic
  const matchesSearch = (value, search) => {
    if (!value) return false;
    return value.toString().toLowerCase().includes(search.toLowerCase());
  };
  const handleGlobalSearch = (value) => {
    setGlobalSearch(value);
    setPage(0);
  };
  const getFilteredData = () => {
    let filteredData = nocSearchData || [];
    // Global search
    if (globalSearch) {
      filteredData = filteredData.filter((item) => {
        const { RegionInfo, Departments } = item;
        return (
          matchesSearch(RegionInfo?.district, globalSearch) ||
          matchesSearch(RegionInfo?.tehsil, globalSearch) ||
          matchesSearch(RegionInfo?.village, globalSearch) ||
          departmentsList.some((dept) => matchesSearch(Departments?.[dept.code], globalSearch))
        );
      });
    }
    // Column filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filteredData = filteredData.filter((item) => {
          const { RegionInfo, Departments } = item;
          if (key === "district") return matchesSearch(RegionInfo?.district, value);
          if (key === "tehsil") return matchesSearch(RegionInfo?.tehsil, value);
          if (key === "village") return matchesSearch(RegionInfo?.village, value);
          // Department columns
          return matchesSearch(Departments?.[key], value);
        });
      }
    });
    return filteredData;
  };

  const filteredData = getFilteredData();
  const columns = allColumns.filter((col) => selectedColumns.includes(col.id));
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  // Popover handlers
  const handleOpenColumnFilter = (event) => setAnchorEl(event.currentTarget);
  const handleCloseColumnFilter = () => setAnchorEl(null);
  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      if (selectedColumns.length > 1) {
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };
  const handleFilterChange = (property, value) => {
    setFilters({
      ...filters,
      [property]: value,
    });
  };
  const handleClearFilter = (property) => {
    setFilters({
      ...filters,
      [property]: "",
    });
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // useForm for onSubmit and field registration
  const { handleSubmit, register, setValue, getValues, watch } = useForm();
  const watchAllFields = watch();

  // Set default values for checkboxes on mount
  useEffect(() => {
    if (!nocSearchData) return;
    nocSearchData.forEach((item, idx) => {
      departmentsList.forEach((dept) => {
        setValue(`noc[${idx}].${dept.code}`, item.Departments?.[dept.code]?.toLowerCase() === "yes");
      });
    });
    // eslint-disable-next-line
  }, [nocSearchData, departmentsList, setValue]);

  const closeToast = () => {
    setShowToast(null);
    setEditMode(false);
    history.push("/digit-ui/employee/obps/noc/view");
    refetch();
  };

  // Custom submit handler to print payload in required format
  const onFormSubmit = async (formData) => {
    // formData.noc is an array of objects: [{acquisitionSection: true, ...}, ...]
    const nocDetails = (nocSearchData || []).map((item, idx) => {
      const { RegionInfo, Departments, ...rest } = item;
      const row = {
        district: RegionInfo?.district || "",
        tehsil: RegionInfo?.tehsil || "",
        village: RegionInfo?.village || "",
        authority: RegionInfo?.authority || "",
        ...rest,
      };
      departmentsList.forEach((dept) => {
        row[dept.code] = formData.noc?.[idx]?.[dept.code] ? "Yes" : "No";
      });
      // Add audit fields if present
      if (item.createdBy) row.createdBy = item.createdBy;
      if (item.createdTime) row.createdTime = item.createdTime;
      if (item.lastModifyBy) row.lastModifyBy = item.lastModifyBy;
      if (item.lastModifiedTime) row.lastModifiedTime = item.lastModifiedTime;
      return row;
    });
    // const nocDetails = { Hello: "Hello" };
    // Print the payload
    try {
      const result = await Digit.OBPSService.NOCExcelUpdate(tenantId, { nocDetails });
      if (result) {
        setShowToast({ key: "success", message: `NOC list updated successfully.` });
      }
    } catch (e) {
      setShowToast({ key: "true", error: true, message: e?.response?.data?.Errors[0]?.message });
    }
  };

  useEffect(() => {
    if (!showToast) return;
    const timeOut = setTimeout(closeToast, 2000);
    return () => clearTimeout(timeOut);
  }, [showToast]);

  if (isLoading) return <Loader />;

  return (
    <React.Fragment>
      <BackButton />
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="emp-table-noc-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" className="emp-table-caption">
            Edit NOC
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleOpenColumnFilter}
              size="small"
              sx={{
                height: "27px",
                fontSize: "12px",
                borderColor: "rgb(31, 74, 124)",
                color: "rgb(31, 74, 124)",
              }}
            >
              {t("FASTPAS.GLOBAL.FILTERS.TEXT")}({selectedColumns.length}/{allColumns.length})
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search here..."
              value={globalSearch}
              onChange={(e) => handleGlobalSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ background: "#2A3F54", color: "#fff", height: "24px", borderRadius: "7px 0px 0px 7px", width: "29px" }} />
                  </InputAdornment>
                ),
                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleGlobalSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                padding: "0px",
                height: "25px",
                "& .MuiInputBase-root": {
                  height: "27px",
                  padding: "0px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgb(31, 74, 124)",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <TableContainer className="table-container">
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseColumnFilter}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              className="table-popover"
            >
              <div className="select-column-popover">
                <Typography variant="subtitle2" gutterBottom>
                  Select Columns to Display
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: "0px" }}
                      checked={selectedColumns.length === allColumns.length}
                      indeterminate={selectedColumns.length > 0 && selectedColumns.length < allColumns.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns(allColumns.map((col) => col.id));
                        } else {
                          setSelectedColumns([allColumns[0].id]);
                        }
                      }}
                    />
                  }
                  sx={{ margin: "0px", fontWeight: "bold" }}
                  label="Select All"
                />
                {allColumns.map((column) => (
                  <div key={column.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          sx={{ padding: "0px" }}
                          checked={selectedColumns.includes(column.id)}
                          onChange={() => handleColumnToggle(column.id)}
                          disabled={column.id === "srNo" && selectedColumns.length === 1}
                        />
                      }
                      sx={{ margin: "0px" }}
                      label={column.label}
                    />
                  </div>
                ))}
              </div>
            </Popover>
            <Table stickyHeader className="application-table noc-table">
              <thead>
                <tr>
                  {columns.map((column) => {
                    let stickyClass = "";
                    if (column.id === "district") stickyClass = "sticky-col";
                    if (column.id === "tehsil") stickyClass = "sticky-col-tehsil";
                    if (column.id === "village") stickyClass = "sticky-col-village";
                    return (
                      <th key={column.id} className={stickyClass}>
                        {column.label}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {columns.map((column) => {
                    let stickyClass = "";
                    if (column.id === "district") stickyClass = "sticky-col";
                    if (column.id === "tehsil") stickyClass = "sticky-col-tehsil";
                    if (column.id === "village") stickyClass = "sticky-col-village";
                    return (
                      <td key={column.id} className={stickyClass}>
                        {column.filterable && (
                          <TextField
                            variant="standard"
                            size="small"
                            value={filters[column.id] || ""}
                            onChange={(e) => handleFilterChange(column.id, e.target.value)}
                            InputProps={{
                              endAdornment: filters[column.id] && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => handleClearFilter(column.id)}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiInput-input": {
                                color: "black !important",
                                fontSize: "0.75rem",
                              },
                              width: "100%",
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} align="center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => {
                    const { RegionInfo } = item;
                    const rowIdx = nocSearchData.indexOf(item);
                    return (
                      <tr key={index}>
                        {columns.map((column) => {
                          let stickyClass = "";
                          if (column.id === "district") stickyClass = "sticky-col";
                          if (column.id === "tehsil") stickyClass = "sticky-col-tehsil";
                          if (column.id === "village") stickyClass = "sticky-col-village";
                          if (column.id === "srNo") return <td key={column.id}>{page * rowsPerPage + index + 1}</td>;
                          if (column.id === "district")
                            return (
                              <td key={column.id} className={stickyClass}>
                                {RegionInfo?.district || "NA"}
                              </td>
                            );
                          if (column.id === "tehsil")
                            return (
                              <td key={column.id} className={stickyClass}>
                                {RegionInfo?.tehsil || "NA"}
                              </td>
                            );
                          if (column.id === "village")
                            return (
                              <td key={column.id} className={stickyClass}>
                                {RegionInfo?.village || "NA"}
                              </td>
                            );
                          // Department columns: render checkbox registered with useForm
                          return (
                            <td key={column.id}>
                              <Checkbox
                                {...register(`noc[${rowIdx}].${column.id}`)}
                                checked={watchAllFields?.noc?.[rowIdx]?.[column.id] || false}
                                color="primary"
                                sx={{ padding: "0px" }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", margin: "1rem 0" }}>
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>
        </div>
        {showToast && (
          <Toast error={showToast?.error} warning={showToast?.warning} label={showToast?.message} onClose={closeToast} isDeleteBtn={true} />
        )}
      </form>
    </React.Fragment>
  );
};

export default EditNoc;
